// Angular Imports
import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';

// project import
import { AdminComponent } from './theme/layout/admin/admin.component';
import { GuestComponent } from './theme/layout/guest/guest.component';
import { AuthGuardChild } from './theme/shared/_helpers/auth.guard';
import { atlasOwnerGuard } from './theme/shared/_helpers/atlas-owner.guard';
import { Role } from './theme/shared/_helpers/role';

const routes: Routes = [
  {
    path: '',
    component: GuestComponent,
    children: [
      {
        path: '',
        redirectTo: '',
        pathMatch: 'full'
      },
      {
        path: '',
        loadComponent: () =>
          import('./demo/pages/authentication/authentication-v1/v1-login/v1-login.component').then((c) => c.V1LoginComponent)
      },
      // {
      //   path: '',
      //   loadComponent: () => import('./demo/pages/landing/landing.component').then((c) => c.LandingComponent)
      // },
      {
        path: 'login',
        loadComponent: () =>
          import('./demo/pages/authentication/authentication-v1/v1-login/v1-login.component').then((c) => c.V1LoginComponent)
      },
      {
        path: 'register',
        loadComponent: () =>
          import('./demo/pages/authentication/authentication-v1/v1-register/v1-register.component').then((c) => c.V1RegisterComponent)
      },
      {
        path: 'forgetPassword',
        loadComponent: () =>
          import('./demo/pages/authentication/authentication-v1/v1-fr-password/v1-fr-password.component').then(
            (c) => c.V1FrPasswordComponent
          )
      },
      {
        path: 'landing',
        loadComponent: () => import('./demo/pages/landing/landing.component').then((c) => c.LandingComponent)
      },
      {
        path: 'maintenance',
        loadChildren: () => import('./demo/pages/maintenance/maintenance.module').then((m) => m.MaintenanceModule)
      },
      {
        path: 'auth',
        canActivateChild: [AuthGuardChild],
        loadChildren: () => import('./demo/pages/authentication/authentication.module').then((m) => m.AuthenticationModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'contact-us',
        loadComponent: () => import('./demo/pages/contact-us/contact-us.component').then((c) => c.ContactUsComponent)
      },
      {
        path: 'faq',
        loadComponent: () => import('./demo/pages/faq/faq.component').then((c) => c.FaqComponent)
      },
      {
        path: 'privacy-policy',
        loadComponent: () => import('./demo/pages/prv-policy/prv-policy.component').then((c) => c.PrvPolicyComponent)
      },
      {
        path: 'unauthorized',
        loadComponent: () =>
          import('./demo/pages/maintenance/unauthorize-error/unauthorize-error.component').then((c) => c.UnauthorizeErrorComponent)
      },
      {
        path: 'pages/:page',
        loadComponent: () =>
          import('./demo/public/content-page/content-page.component').then((c) => c.ContentPageComponent)
      },
      {
        path: 'delete-account',
        loadComponent: () =>
          import('./demo/public/delete-account/delete-account.component').then((c) => c.DeleteAccountComponent)
      }
    ]
  },
  {
    path: '',
    component: AdminComponent,
    canActivateChild: [AuthGuardChild],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'dashboard'
      },
      {
        // Tab shell: the operational dashboard and the AI dashboard are separate
        // child routes so each keeps its own state and data loading.
        path: 'dashboard',
        loadComponent: () =>
          import('./demo/dashboard/dashboard-shell/dashboard-shell.component').then((c) => c.DashboardShellComponent),
        data: { roles: [Role.Admin, Role.User] },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'overview' },
          {
            path: 'overview',
            loadComponent: () => import('./demo/dashboard/dashboard/dashboard.component').then((c) => c.DashboardComponent)
          },
          {
            // Owner-only. The server enforces it on every Atlas call; this just keeps
            // non-Owners off a page that would render nothing but denials.
            path: 'ai-dashboard',
            canActivate: [atlasOwnerGuard],
            loadComponent: () => import('./demo/dashboard/ai-dashboard/ai-dashboard.component').then((c) => c.AiDashboardComponent)
          }
        ]
      },
      {
        // Owner-only, matching the sidebar entry — hiding a link isn't a control.
        path: 'atlas-alerts',
        canActivate: [atlasOwnerGuard],
        loadComponent: () =>
          import('./demo/dashboard/atlas-alerts/atlas-alerts.component').then((c) => c.AtlasAlertsComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'drivers',
        loadChildren: () => import('./demo/dashboard/drivers/drivers.routes').then((r) => r.driversRoutes),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'passengers',
        loadComponent: () => import('./demo/dashboard/passengers/passengers.component').then((c) => c.PassengersComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'passengers/add',
        loadComponent: () => import('./demo/dashboard/passengers/add-passenger/add-passenger.component').then((c) => c.AddPassengerComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'passengers/edit/:id',
        loadComponent: () => import('./demo/dashboard/passengers/add-passenger/add-passenger.component').then((c) => c.AddPassengerComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'passengers/profile/:id',
        loadChildren: () => import('./demo/dashboard/passengers/passenger-profile/passenger-profile.module').then((m) => m.PassengerProfileModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'passengers/pulse/:id',
        loadComponent: () => import('./demo/dashboard/passengers/passenger-pulse/passenger-pulse.component').then((c) => c.PassengerPulseComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'business-users',
        loadChildren: () => import('./demo/dashboard/business-users/business-users.routes').then((r) => r.businessUsersRoutes),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'trips',
        loadComponent: () => import('./demo/dashboard/trips/trips.component').then((c) => c.TripsComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'trips/:id',
        loadComponent: () => import('./demo/dashboard/trips/trip-detail/trip-detail.component').then((c) => c.TripDetailComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'bird-eye-view',
        loadComponent: () => import('./demo/dashboard/bird-eye-view/bird-eye-view.component').then((c) => c.BirdEyeViewComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'finance',
        loadComponent: () => import('./demo/dashboard/finance/finance.component').then((c) => c.FinanceComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'blogs',
        loadChildren: () => import('./demo/dashboard/blogs/blogs.module').then((m) => m.BlogsModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'marketing',
        loadChildren: () => import('./demo/dashboard/marketing-promo/marketing-promo.module').then((m) => m.MarketingPromoModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'settings',
        loadChildren: () => import('./demo/dashboard/settings/settings.module').then((m) => m.SettingsModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'reports',
        loadChildren: () => import('./demo/dashboard/reports/reports.module').then((m) => m.ReportsModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'widget/statistics',
        loadComponent: () => import('./demo/widget/statistics/statistics.component').then((c) => c.StatisticsComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'widget/data',
        loadComponent: () => import('./demo/widget/data/data.component').then((c) => c.DataComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'widget/chart',
        loadComponent: () => import('./demo/widget/widget-chart/widget-chart.component').then((c) => c.WidgetChartComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'layout',
        loadChildren: () => import('./demo/layout/layout.module').then((m) => m.LayoutModule)
      },
      {
        path: 'online-course',
        loadChildren: () => import('./demo/admin-panel/online-courses/online-courses.module').then((m) => m.OnlineCoursesModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'membership',
        loadChildren: () => import('./demo/admin-panel/membership/membership.module').then((m) => m.MembershipModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'helpdesk',
        loadChildren: () => import('./demo/admin-panel/helpdesk/helpdesk.module').then((m) => m.HelpdeskModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'invoice',
        loadChildren: () => import('./demo/admin-panel/invoice/invoice.module').then((m) => m.InvoiceModule),
        data: { role: [Role.Admin, Role.User] }
      },
      {
        path: 'managers',
        loadComponent: () => import('./demo/admin-portal/managers/managers.component').then((c) => c.ManagersComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'user',
        loadChildren: () => import('./demo/application/user/user.module').then((m) => m.UserModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'customer',
        loadChildren: () => import('./demo/application/customer/customer.module').then((m) => m.CustomerModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'chat',
        loadComponent: () => import('./demo/application/chat/chat.component').then((c) => c.ChatComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'kanban',
        loadComponent: () => import('./demo/application/kanban/kanban.component').then((c) => c.KanbanComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'mail',
        loadComponent: () => import('./demo/application/email/email.component').then((c) => c.EmailComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'calender',
        loadComponent: () => import('./demo/application/calender/calender.component').then((c) => c.CalenderComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'contact',
        loadChildren: () => import('./demo/application/contact/contact.module').then((m) => m.ContactModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'ec',
        loadChildren: () => import('./demo/application//ecommerce/ecommerce.module').then((m) => m.EcommerceModule),
        data: { roles: [Role.Admin, Role.User] }
      },

      {
        path: 'typography',
        loadComponent: () => import('./demo/elements/typography/typography.component').then((c) => c.TypographyComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'basic',
        loadChildren: () => import('./demo/elements/basic/basic.module').then((m) => m.BasicModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'advance',
        loadChildren: () => import('./demo/elements/advance/advance.module').then((m) => m.AdvanceModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'forms',
        loadChildren: () => import('./demo/forms/form-elements/form-elements.module').then((m) => m.FormElementsModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'fPlugin',
        loadChildren: () => import('./demo/forms/form-plugin/form-plugin.module').then((m) => m.FormPluginModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'form-validation',
        loadComponent: () => import('./demo/forms/form-validation/form-validation.component').then((c) => c.FormValidationComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'textEditor',
        loadChildren: () => import('./demo/forms/text-editors/text-editors.module').then((m) => m.TextEditorsModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'form-layout',
        loadChildren: () => import('./demo/forms/form-layout/form-layout.module').then((m) => m.FormLayoutModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'fileUpload',
        loadChildren: () => import('./demo/forms/file-upload/file-upload.module').then((m) => m.FileUploadModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'imagesCropper',
        loadComponent: () => import('./demo/forms/image-cropper/image-cropper.component').then((c) => c.ImageCropperComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      // {
      //   path: 'dataTables',
      //   loadComponent: () => import('./demo/tables/tbl-data-table/tbl-data-table.component').then((c) => c.TblDataTableComponent),
      //   data: { roles: [Role.Admin, Role.User] }
      // },
      {
        path: 'ng-table',
        loadChildren: () => import('./demo/tables/ng-bootstrap-table/ng-bootstrap-table.module').then((m) => m.NgBootstrapTableModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'chart',
        loadChildren: () => import('./demo/chart-maps/core-chart/core-chart.module').then((m) => m.CoreChartModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'map',
        loadChildren: () => import('./demo/chart-maps/core-map/core-map.module').then((m) => m.CoreMapModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'samplePage',
        loadComponent: () => import('./demo/other/sample-page/sample-page.component').then((c) => c.SamplePageComponent)
      },
      {
        path: 'price',
        loadChildren: () => import('./demo/pages/price/price.module').then((m) => m.PriceModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'metrics',
        loadChildren: () => import('./demo/dashboard/metrics/metrics.module').then((m) => m.MetricsModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'air-ports',
        loadChildren: () => import('./demo/dashboard/air-ports/air-ports.module').then((m) => m.AirPortsModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'geofencing',
        loadComponent: () => import('./demo/dashboard/air-ports/geofencing/geofencing.component').then((c) => c.GeofencingComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'geofencing/add',
        loadComponent: () => import('./demo/dashboard/air-ports/geofencing/add-geofencing/add-geofencing.component').then((c) => c.AddGeofencingComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'geofencing/edit/:id',
        loadComponent: () => import('./demo/dashboard/air-ports/geofencing/add-geofencing/add-geofencing.component').then((c) => c.AddGeofencingComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'surge',
        loadComponent: () => import('./demo/dashboard/surge/surge.component').then((c) => c.SurgeComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'system-access',
        loadChildren: () => import('./demo/dashboard/system-access/system-access.module').then((m) => m.SystemAccessModule),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'system-access/manage-users/add',
        loadComponent: () => import('./demo/dashboard/system-access/add-user/add-user.component').then((c) => c.AddUserComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'system-access/manage-users/edit/:id',
        loadComponent: () => import('./demo/dashboard/system-access/add-user/add-user.component').then((c) => c.AddUserComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'system-access/roles/add',
        loadComponent: () => import('./demo/dashboard/system-access/add-role/add-role.component').then((c) => c.AddRoleComponent),
        data: { roles: [Role.Admin, Role.User] }
      },
      {
        path: 'system-access/roles/edit/:id',
        loadComponent: () => import('./demo/dashboard/system-access/add-role/add-role.component').then((c) => c.AddRoleComponent),
        data: { roles: [Role.Admin, Role.User] }
      }
    ]
  },
  {
    path: 'shareRide/:tripId',
    loadComponent: () => import('./demo/public/share-ride/share-ride.component').then((c) => c.ShareRideComponent)
  },
  {
    path: 'shareRide/:tripId/:role',
    loadComponent: () => import('./demo/public/share-ride/share-ride.component').then((c) => c.ShareRideComponent)
  },
  {
    path: '**',
    loadComponent: () => import('./demo/pages/maintenance/maintain-error/maintain-error.component').then((c) => c.MaintainErrorComponent)
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {}
